import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { VideoCall, Person, AccessTime, Schedule } from '@mui/icons-material';

/**
 * Student-scoped "Join Class" dialog — presentation only.
 *
 * All values are pre-computed by StudentDashboard: `timeLabel` is already
 * formatted (no date math here), and `onJoinMeeting` carries the side-effect of
 * opening the meeting link. This component performs no API calls, no formatting
 * and holds no state; it renders the confirmation surface shown after the
 * `joinClass` request succeeds.
 */
const DetailRow = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
    <Box
      sx={{
        width: 34,
        height: 34,
        flex: 'none',
        borderRadius: '10px',
        display: 'grid',
        placeItems: 'center',
        color: 'primary.main',
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.13),
        '& svg': { fontSize: 18 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: 'text.primary' }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const JoinClassDialog = ({
  open,
  onClose,
  subject,
  teacherName,
  timeLabel,
  duration,
  meetingLink,
  onJoinMeeting,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
      },
    }}
  >
    <DialogTitle sx={{ pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ backgroundColor: 'primary.main' }}>
          <VideoCall />
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Join Class
        </Typography>
      </Box>
    </DialogTitle>

    <DialogContent sx={{ pt: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
        {subject}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          p: 2,
          mb: 2,
          borderRadius: '14px',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <DetailRow icon={<Person />} label="Teacher" value={teacherName} />
        <DetailRow icon={<AccessTime />} label="Time" value={timeLabel} />
        <DetailRow icon={<Schedule />} label="Duration" value={`${duration} minutes`} />
      </Box>

      {meetingLink && (
        <Box
          sx={{
            p: 3,
            borderRadius: '14px',
            textAlign: 'center',
            bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
          }}
        >
          <Typography variant="body2" gutterBottom>
            Click the button below to join your class:
          </Typography>
          <Button
            variant="contained"
            color="success"
            startIcon={<VideoCall />}
            onClick={onJoinMeeting}
            fullWidth
            sx={{ borderRadius: '12px', py: 1.5, mt: 2, textTransform: 'none' }}
          >
            Join Google Meet
          </Button>
        </Box>
      )}
    </DialogContent>

    <DialogActions sx={{ p: 3, pt: 0 }}>
      <Button onClick={onClose} sx={{ borderRadius: '12px', px: 3, textTransform: 'none' }}>
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

export default JoinClassDialog;
